import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PROGRAMS } from "@/lib/content";
import { PROGRAMS_HI } from "@/lib/content_hi";

const ADDRESS = {
  en: "RoboSpark PrP Pvt Ltd, C-30, C-Block, Sector 63, Noida-201301 (U.P.)",
  hi: "RoboSpark PrP Pvt Ltd, C-30, C-ब्लॉक, सेक्टर 63, नोएडा-201301 (उ.प्र.)",
};

const T = {
  en: { kicker: "Curriculum Brochure · Classes 6–12", path: "Grade-wise learning path", outcomes: "What students will achieve", tools: "Tools & platforms", plans: "Annual fees (Classroom + Lab Assistance only; kits separate) + 18% GST", le: "Learn & Earn", leText: "Top student projects are refined into RoboSpark-branded products and sold through Roboshop — students earn a share of every sale.", contact: "Contact us", forSchools: "Schools: Municipal ₹1,50,000/year or ₹20,000/month · Rural ₹1,00,000/year or ₹12,000/month · 20–30% off on new onboarding · bulk roster and one central bill. Coupons (new admissions, till 14 November 2026): 50% or 70% off only · Section A (Class 6, 7, 8) max 1,000 · Section B (9, 10) max 500 · Section C (11, 12) max 500 · one-time use, valid 1 hour or until used · codes like Robo-A-100.", plansList: [["Class 6–7", "₹4,999/year"], ["Class 8–9–10", "₹6,999/year"], ["Class 11–12", "₹10,999/year"]] },
  hi: { kicker: "पाठ्यक्रम ब्रोशर · कक्षा 6–12", path: "कक्षावार सीखने का मार्ग", outcomes: "छात्र क्या हासिल करेंगे", tools: "टूल्स और प्लेटफ़ॉर्म", plans: "वार्षिक फ़ीस (केवल क्लासरूम + लैब सहायता; किट अलग) + 18% GST", le: "सीखें और कमाएँ", leText: "छात्रों के सर्वश्रेष्ठ प्रोजेक्ट RoboSpark ब्रांड उत्पाद बनकर Roboshop पर बिकते हैं — हर बिक्री में छात्रों को हिस्सा मिलता है।", contact: "संपर्क करें", forSchools: "स्कूल: नगरपालिका ₹1,50,000/वर्ष या ₹20,000/माह · ग्रामीण ₹1,00,000/वर्ष या ₹12,000/माह · नए ऑनबोर्डिंग पर 20–30% छूट · बल्क रोस्टर और एक केंद्रीकृत बिल। कूपन (नए दाखिले, 14 नवंबर 2026 तक): केवल 50% या 70% छूट · सेक्शन A (कक्षा 6, 7, 8) अधिकतम 1,000 · सेक्शन B (9, 10) अधिकतम 500 · सेक्शन C (11, 12) अधिकतम 500 · एक बार उपयोग, 1 घंटे या उपयोग तक मान्य · कोड जैसे Robo-A-100।", plansList: [["कक्षा 6–7", "₹4,999/वर्ष"], ["कक्षा 8–9–10", "₹6,999/वर्ष"], ["कक्षा 11–12", "₹10,999/वर्ष"]] },
};

const H = ({ children }) => <h3 style={{ fontSize: 16, fontWeight: 800, color: "#0A192F", borderLeft: "4px solid #FF2E63", paddingLeft: 10, margin: "22px 0 10px" }}>{children}</h3>;

const BrochureDoc = ({ slug, lang, innerRef }) => {
  const base = PROGRAMS[slug];
  const p = lang === "hi" ? { ...base, ...PROGRAMS_HI[slug] } : base;
  const t = T[lang];
  return (
    <div ref={innerRef} style={{ width: 794, background: "#fff", color: "#1e293b", fontFamily: "'Noto Sans Devanagari','Manrope',sans-serif", fontSize: 12, lineHeight: 1.6 }}>
      <div style={{ background: "#0A192F", color: "#fff", padding: "28px 36px" }}>
        <div style={{ fontSize: 20, fontWeight: 800 }}><span style={{ color: "#FF2E63" }}>Robo</span>Spark <span style={{ fontSize: 10, opacity: 0.6, letterSpacing: 2 }}>PRP PVT LTD</span></div>
        <div style={{ marginTop: 18, color: "#00E5FF", fontSize: 11, fontWeight: 700, letterSpacing: 1.5 }}>{t.kicker}</div>
        <div style={{ fontSize: 28, fontWeight: 800, marginTop: 4 }}>{p.title}</div>
        <div style={{ marginTop: 6, opacity: 0.8, fontSize: 13 }}>{p.tagline}</div>
      </div>
      <div style={{ padding: "8px 36px 24px" }}>
        <H>{t.path}</H>
        <div style={{ display: "flex", gap: 10 }}>
          {p.levels.map((l) => (
            <div key={l.grade} style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, pageBreakInside: "avoid" }}>
              <div style={{ color: "#FF2E63", fontWeight: 700, fontSize: 11 }}>{l.grade}</div>
              <div style={{ fontWeight: 800, fontSize: 14, color: "#0A192F" }}>{l.name}</div>
              <ol style={{ margin: "6px 0 0", paddingLeft: 16 }}>{l.topics.map((x) => <li key={x}>{x}</li>)}</ol>
            </div>
          ))}
        </div>
        <H>{t.outcomes}</H>
        <ul style={{ margin: 0, paddingLeft: 18, columns: 2 }}>{p.outcomes.map((o) => <li key={o}>{o}</li>)}</ul>
        <H>{t.tools}</H>
        <div>{p.tools.map((x) => <span key={x} style={{ display: "inline-block", border: "1px solid #cbd5e1", borderRadius: 999, padding: "2px 10px", margin: "0 6px 6px 0", fontWeight: 600 }}>{x}</span>)}</div>
        <div style={{ display: "flex", gap: 14, pageBreakInside: "avoid" }}>
          <div style={{ flex: 1 }}>
            <H>{t.plans}</H>
            {t.plansList.map(([n, v]) => (
              <div key={n} style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #e2e8f0", padding: "5px 0" }}><span>{n}</span><b>{v}</b></div>
            ))}
          </div>
          <div style={{ flex: 1 }}>
            <H>{t.le}</H>
            <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 10, padding: 12 }}>{t.leText}</div>
          </div>
        </div>
        <div style={{ marginTop: 14, padding: 12, background: "#f1f5f9", borderRadius: 10 }}>{t.forSchools}</div>
        <div style={{ marginTop: 18, background: "#0A192F", color: "#fff", borderRadius: 10, padding: "14px 16px", pageBreakInside: "avoid" }}>
          <div style={{ fontWeight: 800, color: "#FF2E63" }}>{t.contact}</div>
          <div>{ADDRESS[lang]}</div>
          <div>robospark.prp@gmail.com</div>
          <div>Call: 9608321115 · WhatsApp: 8100499334</div>
        </div>
      </div>
    </div>
  );
};

export const BrochureButtons = ({ slug }) => {
  const [busy, setBusy] = useState(null);
  const refs = { en: useRef(null), hi: useRef(null) };

  const download = async (lang) => {
    setBusy(lang);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      await html2pdf().set({
        margin: 0,
        filename: `RoboSpark-${PROGRAMS[slug].key}-Brochure-${lang === "hi" ? "Hindi" : "English"}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "px", format: [794, 1123], orientation: "portrait", hotfixes: ["px_scaling"] },
        pagebreak: { mode: ["css", "avoid-all"] },
      }).from(refs[lang].current).save();
    } catch (e) {
      toast.error("Could not create PDF. Please try again.");
    } finally { setBusy(null); }
  };

  return (
    <>
      <div className="flex flex-wrap gap-3" data-testid="brochure-buttons">
        <Button onClick={() => download("hi")} disabled={!!busy} size="lg" variant="outline" className="rounded-full bg-slate-900/[0.07] border-slate-400 text-[#0A192F] hover:bg-white hover:text-[#0A192F]" data-testid="brochure-hi-btn">
          {busy === "hi" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileDown className="w-4 h-4 mr-1" />}हिंदी ब्रोशर (PDF)
        </Button>
        <Button onClick={() => download("en")} disabled={!!busy} size="lg" variant="outline" className="rounded-full bg-slate-900/[0.07] border-slate-400 text-[#0A192F] hover:bg-white hover:text-[#0A192F]" data-testid="brochure-en-btn">
          {busy === "en" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileDown className="w-4 h-4 mr-1" />}English Brochure (PDF)
        </Button>
      </div>
      <div aria-hidden style={{ position: "fixed", left: -10000, top: 0 }}>
        <BrochureDoc slug={slug} lang="hi" innerRef={refs.hi} />
        <BrochureDoc slug={slug} lang="en" innerRef={refs.en} />
      </div>
    </>
  );
};
